class customVariant extends HTMLElement {
  constructor() {
    super();
    this.selected = {};
  }

  connectedCallback() {
    this.variants = this.getJson('#json_prod_var');
    this.options = this.getJson('#json_prod_options');

    if (!this.variants || !this.options) return;

    this.setupSizeButtons();
    this.setupColorRadios();
    this.setupVariantCards();
    this.setupOptionChips();
    this.setupDropdowns();
    this.setupSellingPlanListener();

    // Preselect by variant ID first (sets all options at once)
    // This should run before preselectColor to avoid partial selection issues
    const variantIdPreselected = this.preselectVariantById();

    // Only preselect color if variant ID preselection didn't work
    // This handles cases where variant ID might not be available initially
    if (!variantIdPreselected) {
      this.preselectColor();
    }

    // Ensure price is updated on initial load to match listing page (fix for issue #1)
    // This handles cases where Liquid-rendered price might differ from JavaScript-updated price
    this.ensurePriceSyncOnLoad();
  }

  ensurePriceSyncOnLoad() {
    // Wait for DOM to be fully ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.syncPriceOnLoad();
      });
    } else {
      // Use setTimeout to ensure all other scripts have run
      setTimeout(() => {
        this.syncPriceOnLoad();
      }, 100);
    }
  }

  syncPriceOnLoad() {
    const variantIdEl = document.querySelector('#product-variant-id');
    if (!variantIdEl) return;

    let variantId = parseInt(variantIdEl.value, 10);
    let variant = null;
    if (variantId && !isNaN(variantId)) {
      variant = this.variants.find(v => v.id === variantId);
    }

    // Pack Bundle case: form input holds bundle id; resolve canonical for content-only updates.
    if (!variant) {
      const canonicalEl = document.querySelector('#json_canonical_variant_id');
      if (canonicalEl) {
        try {
          const canonicalId = parseInt(JSON.parse(canonicalEl.textContent), 10);
          if (canonicalId && !isNaN(canonicalId)) {
            variant = this.variants.find(v => v.id === canonicalId);
          }
        } catch (e) { /* ignore */ }
      }
    }

    if (!variant) return;

    // When the Pack section owns the displayed price, don't overwrite Liquid's SSR price.
    if (!this.hasPackSection()) {
      this.updatePriceDisplay(variant);
    }
    this.updateProductPoints(variant);
    this.updateProductDescription(variant);
    this.updateProductTitle(variant);
    this.updateProductHero(variant);
    this.updateProductComparison(variant);
    this.updateProductFaq(variant);
  }

  hasPackSection() {
    return !!document.querySelector('pack-bundle-section');
  }

  getJson(selector) {
    const el = document.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  setupSizeButtons() {
    // Find the actual size option name (case-insensitive)
    const sizeOptionName = this.options.find(opt => {
      const optNameLower = opt.name.toLowerCase();
      return optNameLower === 'size' || optNameLower === 'sizes';
    })?.name;

    if (!sizeOptionName) return;

    this.querySelectorAll('.size-button-list .size-button').forEach(button => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';

      button.addEventListener('click', () => {
        const value = button.dataset.size;
        button.closest('.size-button-list')
          .querySelectorAll('.size-button')
          .forEach(b => b.classList.remove('active'));
        button.classList.add('active');
        this.selected[sizeOptionName] = value;
        this.updateSelectedVariant();
      });
    });
  }

  /**
   * Wire up chip-style option selectors (e.g. Weight Capacity) rendered when the
   * option block style is "Variant Card (Figma Design)" and the option is not
   * color/size. Mirrors setupSizeButtons: updates `this.selected[name]`, toggles
   * `.option-chip--active`, and triggers `updateSelectedVariant()`.
   */
  setupOptionChips() {
    this.querySelectorAll('.option-chips').forEach(group => {
      const optionName = group.dataset.optionBlockName;
      if (!optionName) return;

      group.querySelectorAll('.option-chip').forEach(chip => {
        if (chip.dataset.initialized === 'true') return;
        chip.dataset.initialized = 'true';

        chip.addEventListener('click', (e) => {
          e.preventDefault();
          if (chip.disabled) return;
          const value = chip.dataset.value;
          group.querySelectorAll('.option-chip').forEach(c => {
            c.classList.remove('option-chip--active');
            c.setAttribute('aria-pressed', 'false');
          });
          chip.classList.add('option-chip--active');
          chip.setAttribute('aria-pressed', 'true');
          this.selected[optionName] = value;
          this.updateSelectedVariant();
        });
      });
    });
  }

  setupColorRadios() {
    // Find the actual color option name (case-insensitive)
    const colorOptionName = this.options.find(opt => {
      const optNameLower = opt.name.toLowerCase();
      return optNameLower === 'color' || optNameLower === 'colors';
    })?.name;

    if (!colorOptionName) return;

    this.querySelectorAll('input[name="option_color"]').forEach(radio => {
      if (radio.dataset.initialized === 'true') return;
      radio.dataset.initialized = 'true';

      radio.addEventListener('change', () => {
        const value = radio.value;
        this.selected[colorOptionName] = value;

        const wrapper = radio.closest('.color-circles-active');
        const labelEl = wrapper?.querySelector('.active-color');
        if (labelEl) labelEl.textContent = value;

        this.updateSelectedVariant();
      });
    });
  }

  setupVariantCards() {
    const variantCards = this.querySelectorAll('.variant-card');
    if (variantCards.length === 0) return;

    variantCards.forEach(card => {
      if (card.dataset.initialized === 'true') return;
      card.dataset.initialized = 'true';

      const button = card.querySelector('.variant-card-button');
      if (!button) return;

      // Skip disabled cards
      if (card.dataset.disabled === 'true' || button.disabled) return;

      const handleCardClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const optionName = card.dataset.optionName;
        const optionValue = card.dataset.optionValue;
        const variantImage = card.dataset.variantImage;

        if (!optionName || !optionValue) return;

        // Remove active state from all cards in the same list
        const cardList = card.closest('.variant-card-list');
        if (cardList) {
          cardList.querySelectorAll('.variant-card').forEach(c => {
            c.classList.remove('active');
          });
        }

        // Add active state to clicked card
        card.classList.add('active');

        // Update selected option
        this.selected[optionName] = optionValue;

        // Update main product image immediately if variant image is available
        if (variantImage) {
          const gallery = document.querySelector('.gallery-section');
          const mainImage = gallery?.querySelector('.main-image img');
          if (mainImage) {
            mainImage.src = variantImage;
            mainImage.removeAttribute('srcset');
          }
        }

        // Update variant selection
        this.updateSelectedVariant();
      };

      // Make both card and button clickable
      card.addEventListener('click', handleCardClick);
      button.addEventListener('click', handleCardClick);
    });
  }

  setupDropdowns() {
    this.querySelectorAll('.custom-select').forEach(select => {
      if (select.dataset.initialized === 'true') return;
      select.dataset.initialized = 'true';

      const trigger = select.querySelector('.select-trigger');
      const options = select.querySelectorAll('.option-value');
      const optionGroup = select.closest('.option-group');
      const label = optionGroup?.querySelector('label')?.textContent?.trim();
      const optionName = label?.replace(/:$/, '');

      if (!trigger || !optionName) return;

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        select.classList.toggle('open');
      });

      options.forEach(opt => {
        opt.addEventListener('click', () => {
          const value = opt.dataset.value;
          const optionName = opt.dataset.optionName;
          if (value && optionName) {
            trigger.innerHTML = opt.innerHTML;
            select.classList.remove('open');
            this.selected[optionName] = value;
            this.updateSelectedVariant();
          }
        });
      });

      document.addEventListener('click', (e) => {
        if (!select.contains(e.target)) {
          select.classList.remove('open');
        }
      });
    });
  }

  preselectColor() {
    const preselected = this.querySelector('input[name="option_color"]:checked');
    if (preselected) {
      preselected.dispatchEvent(new Event('change'));
    }

    // Also preselect variant card if variant ID is available
    const variantIdEl = document.querySelector('#product-variant-id');
    if (variantIdEl && variantIdEl.value) {
      const variantId = parseInt(variantIdEl.value, 10);
      if (variantId && !isNaN(variantId)) {
        this.updateVariantCardStates(variantId);
      }
    }
  }

  updateVariantCardStates(activeVariantId) {
    // Remove active state from all variant cards
    this.querySelectorAll('.variant-card').forEach(card => {
      card.classList.remove('active');
    });

    // Add active state to the matching variant card
    if (activeVariantId) {
      const activeCard = this.querySelector(`.variant-card[data-variant-id="${activeVariantId}"]`);
      if (activeCard) {
        activeCard.classList.add('active');
      }
    }
  }

  updateSelectedVariant() {
    // Check if all options are selected before trying to match
    const allOptionsSelected = this.options.every(opt => {
      const value = this.selected[opt.name];
      return value !== null && value !== undefined && value !== '';
    });

    // Don't try to match if not all options are selected yet
    if (!allOptionsSelected) {
      return;
    }

    const ordered = this.options.map(opt => this.selected[opt.name] || null);
    const match = this.variants.find(v => JSON.stringify(v.options) === JSON.stringify(ordered));

    if (match) {
      const variantIdEl = document.querySelector('#product-variant-id');
      if (!variantIdEl) return;

      const packPresent = this.hasPackSection();

      if (!packPresent) {
        // Standard flow: form id == canonical variant id.
        variantIdEl.value = match.id;
        variantIdEl.dispatchEvent(new Event('change'));

        if (match.available) {
          variantIdEl.removeAttribute('disabled');
        } else {
          variantIdEl.setAttribute('disabled', 'disabled');
        }
      }

      this.updateVariantCardStates(match.id);
      this.updateVariantImage(match);

      if (packPresent) {
        // Hand off pricing + form id + button enabled-state to the pack section so the
        // displayed price/ATC stay in sync with the chosen pack-size for the new weight.
        document.dispatchEvent(new CustomEvent('pack:weight-change', {
          detail: { weight: match.option1, canonicalVariantId: match.id }
        }));
      } else {
        this.updatePriceDisplay(match);
        this.updateButtonState(match);
      }

      this.updateStockMessage(match);
      this.updateProductPoints(match);
      this.updateProductDescription(match);
      this.updateProductTitle(match);
      this.updateProductHero(match);
      this.updateProductComparison(match);
      this.updateProductFaq(match);
      this.updateLineItemImageProperty(match);
    } else {
      // No matching variant found - mark as unavailable
      this.updateButtonState(null);
    }
  }

  /**
   * Sync the hidden `properties[_image]` line-item-property inputs with the
   * currently-selected parent variant's featured image. The cart drawer reads
   * this snapshot so the displayed thumbnail matches the variant the customer
   * actually saw at add-to-cart time. Critical for pack-bundle products where
   * the cart line item is a child product without its own variant image.
   */
  updateLineItemImageProperty(variant) {
    if (!variant) return;

    const url =
      variant.featured_image?.src ||
      variant.featured_image?.url ||
      variant.featured_media?.preview_image?.src ||
      variant.featured_media?.preview_image?.url ||
      '';

    if (!url) return;

    const inputs = document.querySelectorAll('input.line-item-property-image');
    inputs.forEach((input) => {
      if (input.value !== url) {
        input.value = url;
      }
    });
  }

  updateVariantImage(variant) {
    if (!variant) return;

    const featuredImage =
      variant.featured_image?.src ||
      variant.featured_image?.url ||
      variant.featured_media?.preview_image?.src ||
      variant.featured_media?.preview_image?.url ||
      '';

    if (!featuredImage) return;

    const gallery = document.querySelector('.gallery-section');
    if (!gallery) return;

    const mainImage = gallery.querySelector('.main-image img');
    if (!mainImage) return;

    const normalizeImageUrl = (url) => {
      if (!url) return '';
      return url
        .split('?')[0]
        .replace(/^https?:/, '')
        .replace(/^\/\//, '')
        .toLowerCase();
    };

    console.log('featuredImage', featuredImage);
    console.log('normalizeImageUrl', normalizeImageUrl(featuredImage));

    const target = normalizeImageUrl(featuredImage);
    if (!target) return;

    const thumbnails = Array.from(gallery.querySelectorAll('.thumbnails .thumbnail'));
    const matchedThumb = thumbnails.find((thumb) => {
      const thumbUrl = normalizeImageUrl(thumb.dataset.mainImage);
      return thumbUrl && thumbUrl === target;
    });

    if (matchedThumb) {
      matchedThumb.click();
      return;
    }

    mainImage.src = featuredImage;
    mainImage.removeAttribute('srcset');
  }

  updateStockMessage(variant) {
    const instockEl = document.querySelector('.price-block .instock');
    if (!instockEl) {
      return;
    }

    // Get existing text elements
    let instockTextEl = instockEl.querySelector('.instock-text');
    let outstockTextEl = instockEl.querySelector('.outstock-text');
    let svgEl = instockEl.querySelector('svg');

    // Get data attributes from container
    const instockText = instockEl.dataset.instockText || (instockTextEl && instockTextEl.dataset.instockText);
    const outstockText = instockEl.dataset.outstockText || (instockTextEl && instockTextEl.dataset.outstockText);
    const instockColor = instockEl.dataset.instockColor || (instockTextEl && instockTextEl.dataset.instockColor) || '#34A853';
    const outstockColor = instockEl.dataset.outstockColor || (instockTextEl && instockTextEl.dataset.outstockColor) || '#FF0000';

    // Determine if variant is available
    // Check inventory management and policy (matching Liquid logic)
    const inventoryManagement = variant.inventory_management;
    const inventoryPolicy = variant.inventory_policy;
    const inventoryQuantity = variant.inventory_quantity != null ? parseInt(variant.inventory_quantity, 10) : 0;

    // Handle variant.available - could be boolean, string, or undefined
    let variantAvailable = true; // Default to true
    if (variant.available !== undefined && variant.available !== null) {
      if (typeof variant.available === 'boolean') {
        variantAvailable = variant.available;
      } else if (typeof variant.available === 'string') {
        variantAvailable = variant.available.toLowerCase() === 'true' || variant.available === '1';
      } else {
        variantAvailable = Boolean(variant.available);
      }
    }

    let showInStock = false;

    if (inventoryManagement === 'shopify') {
      if (inventoryPolicy === 'continue') {
        // Allow backorders - always show in stock
        showInStock = true;
      } else if (inventoryPolicy === 'deny') {
        // Don't allow backorders - check quantity
        // If inventory_quantity is null/undefined, it means inventory is not tracked, so use variant.available
        if (variant.inventory_quantity == null || variant.inventory_quantity === undefined) {
          showInStock = variantAvailable;
        } else {
          // Inventory is tracked, check if quantity > 0
          showInStock = inventoryQuantity > 0 && variantAvailable;
        }
      } else {
        // inventoryPolicy is undefined/null - use variant.available as primary indicator
        // This handles cases where policy isn't explicitly set but variant says it's available
        showInStock = variantAvailable;
      }
    } else {
      // No inventory management - use variant.available
      showInStock = variantAvailable;
    }

    // Update the display based on structure
    if (showInStock) {
      // Show in-stock message

      // Create instock-text if it doesn't exist
      if (!instockTextEl && instockText) {
        instockTextEl = document.createElement('div');
        instockTextEl.className = 'instock-text';
        instockEl.appendChild(instockTextEl);
      }

      if (instockTextEl) {
        instockTextEl.textContent = instockText || instockTextEl.textContent;
        instockTextEl.style.setProperty('--instock-color', instockColor);
        instockTextEl.style.cssText += 'display: block !important; visibility: visible !important; opacity: 1 !important;';
      }

      // Create SVG if it doesn't exist (for yas-product-block structure)
      if (!svgEl && instockColor) {
        svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svgEl.setAttribute('width', '25');
        svgEl.setAttribute('height', '25');
        svgEl.setAttribute('viewBox', '0 0 25 25');
        svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svgEl.style.cssText = 'overflow: visible; display: block;';

        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', '12.5');
        circle.setAttribute('cy', '12.5');
        circle.setAttribute('r', '4');
        circle.setAttribute('fill', instockColor);
        svgEl.appendChild(circle);

        // Add ripple circles
        for (let i = 0; i < 3; i++) {
          const ripple = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          ripple.setAttribute('class', 'ripple');
          ripple.setAttribute('cx', '12.5');
          ripple.setAttribute('cy', '12.5');
          ripple.setAttribute('r', '4');
          ripple.setAttribute('fill', 'none');
          ripple.setAttribute('stroke', instockColor);
          ripple.setAttribute('stroke-width', '1');
          svgEl.appendChild(ripple);
        }

        // Insert SVG before instock-text if it exists, otherwise append
        if (instockTextEl) {
          instockEl.insertBefore(svgEl, instockTextEl);
        } else {
          instockEl.appendChild(svgEl);
        }
      }

      if (svgEl) {
        svgEl.style.cssText += 'display: block !important; visibility: visible !important;';
        const circle = svgEl.querySelector('circle:not(.ripple)');
        if (circle) {
          circle.setAttribute('fill', instockColor);
        }
        const ripples = svgEl.querySelectorAll('circle.ripple');
        ripples.forEach(ripple => {
          ripple.setAttribute('stroke', instockColor);
        });
      }

      // Hide outstock-text if it exists
      if (outstockTextEl) {
        outstockTextEl.style.cssText += 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
      }
    } else {
      // Show out-of-stock message

      // Create outstock-text if it doesn't exist
      if (!outstockTextEl && outstockText) {
        outstockTextEl = document.createElement('div');
        outstockTextEl.className = 'outstock-text';
        instockEl.appendChild(outstockTextEl);
      }

      if (outstockTextEl) {
        outstockTextEl.textContent = outstockText || outstockTextEl.textContent;
        outstockTextEl.style.setProperty('--outstock-color', outstockColor);
        outstockTextEl.style.cssText += 'display: block !important; visibility: visible !important; opacity: 1 !important;';
      }

      // Hide instock-text if it exists
      if (instockTextEl) {
        if (outstockTextEl) {
          // If there's a separate outstock element (yas-product-block.liquid structure), hide instock
          instockTextEl.style.cssText += 'display: none !important; visibility: hidden !important; opacity: 0 !important;';
        } else {
          // If instock-text is the only element (yas-product-section.liquid structure), update its content
          if (instockTextEl.dataset.outstockText || outstockText) {
            instockTextEl.textContent = outstockText || instockTextEl.dataset.outstockText;
            instockTextEl.style.setProperty('--instock-color', outstockColor);
            instockTextEl.style.cssText += 'display: block !important; visibility: visible !important; opacity: 1 !important;';
          } else {
            instockTextEl.style.cssText += 'display: none !important; visibility: hidden !important;';
          }
        }
      }

      // Update SVG color to outstock color instead of hiding it
      if (svgEl) {
        svgEl.style.cssText += 'display: block !important; visibility: visible !important;';
        const circle = svgEl.querySelector('circle:not(.ripple)');
        if (circle) {
          circle.setAttribute('fill', outstockColor);
        }
        const ripples = svgEl.querySelectorAll('circle.ripple');
        ripples.forEach(ripple => {
          ripple.setAttribute('stroke', outstockColor);
        });
      } else if (outstockColor) {
        // Create SVG if it doesn't exist (for yas-product-block structure)
        svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svgEl.setAttribute('width', '25');
        svgEl.setAttribute('height', '25');
        svgEl.setAttribute('viewBox', '0 0 25 25');
        svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        svgEl.style.cssText = 'overflow: visible; display: block;';

        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', '12.5');
        circle.setAttribute('cy', '12.5');
        circle.setAttribute('r', '4');
        circle.setAttribute('fill', outstockColor);
        svgEl.appendChild(circle);

        // Add ripple circles
        for (let i = 0; i < 3; i++) {
          const ripple = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          ripple.setAttribute('class', 'ripple');
          ripple.setAttribute('cx', '12.5');
          ripple.setAttribute('cy', '12.5');
          ripple.setAttribute('r', '4');
          ripple.setAttribute('fill', 'none');
          ripple.setAttribute('stroke', outstockColor);
          ripple.setAttribute('stroke-width', '1');
          svgEl.appendChild(ripple);
        }

        // Insert SVG before outstock-text if it exists, otherwise append
        if (outstockTextEl) {
          instockEl.insertBefore(svgEl, outstockTextEl);
        } else {
          instockEl.appendChild(svgEl);
        }
      }
    }
  }

  updateProductPoints(variant) {
    const pointsContainer = document.querySelector('.product-points-content');
    if (!pointsContainer) return;

    const pointsList = pointsContainer.querySelector('.product-points-list');
    if (!pointsList) return;

    try {
      // Get variant points data
      const variantPointsData = JSON.parse(pointsContainer.dataset.variantPoints || '[]');
      const productPoints = JSON.parse(pointsContainer.dataset.productPoints || '[]');
      const showCheckmark = pointsContainer.dataset.showCheckmark === 'true';
      const textColor = pointsContainer.dataset.textColor || '';

      // Find points for current variant
      const variantPointsObj = variantPointsData.find(vp => vp.id === variant.id);
      const pointsToDisplay = variantPointsObj ? variantPointsObj.points : productPoints;

      // Don't update if no points to display
      if (!pointsToDisplay || pointsToDisplay.length === 0) {
        return;
      }

      // Build new points HTML
      let pointsHTML = '';
      pointsToDisplay.forEach(point => {
        pointsHTML += '<li>';
        if (showCheckmark) {
          pointsHTML += '<span class="material-symbols-outlined">check</span>';
        }
        pointsHTML += point;
        pointsHTML += '</li>';
      });

      // Update the list
      pointsList.innerHTML = pointsHTML;
      
      // Update text color if specified
      if (textColor) {
        pointsList.style.color = textColor;
      }
    } catch (error) {
      console.error('Error updating product points:', error);
    }
  }

  updateProductDescription(variant) {
    const descriptionContainer = document.querySelector('.content-section.accordion-section[data-section-type="content"]');
    if (!descriptionContainer) return;

    const descriptionContent = descriptionContainer.querySelector('.product-description-content');
    if (!descriptionContent) return;

    try {
      // Get the data from the JSON script tag
      const descriptionId = descriptionContainer.dataset.descriptionId;
      const scriptTag = document.querySelector(`#variant-descriptions-data-${descriptionId}`);
      
      if (!scriptTag) {
        console.warn('Description data script tag not found');
        return;
      }

      const descriptionsData = JSON.parse(scriptTag.textContent);
      const variantDescriptionsData = descriptionsData.variantDescriptions || [];
      const productDescription = descriptionsData.productDescription || '';
      const textColor = descriptionsData.textColor || '';

      // Find description for current variant
      const variantDescriptionObj = variantDescriptionsData.find(vd => vd.id === variant.id);
      const descriptionToDisplay = variantDescriptionObj ? variantDescriptionObj.description : productDescription;

      // Set the HTML content directly
      descriptionContent.innerHTML = descriptionToDisplay;
      
      // Update text color if specified
      if (textColor) {
        descriptionContent.style.color = textColor;
      }
    } catch (error) {
      console.error('Error updating product description:', error);
    }
  }

  /**
   * Update the product title (h2) and sub-heading (h4.sub-heading) inside the
   * `.heading` block based on the selected variant. Reads per-variant overrides
   * from the JSON island emitted by the `title` block in
   * `sections/yas-product-section.liquid`. Variants without an explicit
   * override fall back to the product canonical values.
   */
  updateProductTitle(variant) {
    if (!variant) return;
    const heading = document.querySelector('.main-product-section .heading[data-title-id]');
    if (!heading) return;

    const titleId = heading.dataset.titleId;
    const scriptTag = document.querySelector(`#variant-title-data-${titleId}`);
    if (!scriptTag) return;

    let data;
    try {
      data = JSON.parse(scriptTag.textContent);
    } catch (e) {
      return;
    }

    const override = (data.variantTitles || []).find(v => v.id === variant.id);
    const titleEl = heading.querySelector('.title h2, .title .product-title');
    const subHeadingEl = heading.querySelector('h4.sub-heading');

    const titleValue = (override && override.title) || data.productTitle || '';
    const shortDescriptionValue =
      (override && override.shortDescription) || data.productShortDescription || '';

    if (titleEl) {
      titleEl.textContent = titleValue;
    }

    if (subHeadingEl) {
      // The metafield can be rich-text (HTML) when present.
      subHeadingEl.innerHTML = shortDescriptionValue;
      subHeadingEl.style.display = shortDescriptionValue ? '' : 'none';
    }
  }

  /**
   * Swap the inner HTML of `.product-hero_container` to match the selected
   * variant. Uses the `<template data-product-hero-template="...">` elements
   * emitted by `sections/yas-product-hero.liquid`. A variant without its own
   * template falls back to the "default" template (product canonical).
   */
  updateProductHero(variant) {
    if (!variant) return;
    this.swapTemplateContent({
      containerSelector: '[data-product-hero-container]',
      templateAttr: 'data-product-hero-template',
      variantId: variant.id
    });
  }

  /**
   * Swap the inner HTML of `.main-comparison-section_container` for the
   * selected variant. Uses templates emitted by
   * `sections/yas-comparison-section.liquid`.
   */
  updateProductComparison(variant) {
    if (!variant) return;
    this.swapTemplateContent({
      containerSelector: '[data-product-comparison-container]',
      templateAttr: 'data-product-comparison-template',
      variantId: variant.id
    });
  }

  /**
   * Swap the inner HTML of the FAQ list (.faq-item-w) for the selected
   * variant. Uses templates emitted by `sections/yas-product-faq.liquid`.
   * The FAQ section's click handler uses event delegation, so it keeps
   * working after the innerHTML swap.
   */
  updateProductFaq(variant) {
    if (!variant) return;
    this.swapTemplateContent({
      containerSelector: '[data-product-faq-container]',
      templateAttr: 'data-product-faq-template',
      variantId: variant.id
    });
  }

  /**
   * Shared helper that replaces a container's innerHTML with the contents of
   * a matching `<template>` element. If no variant-specific template exists,
   * falls back to the template tagged `default`.
   */
  swapTemplateContent({ containerSelector, templateAttr, variantId }) {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const variantTemplate = document.querySelector(`template[${templateAttr}="${variantId}"]`);
    const defaultTemplate = document.querySelector(`template[${templateAttr}="default"]`);
    const template = variantTemplate || defaultTemplate;
    if (!template) return;

    // Skip the swap if the same template is already rendered to avoid
    // unnecessary DOM churn (e.g. reselecting the same variant).
    const currentKey = container.dataset.activeVariantTemplate;
    const newKey = template.getAttribute(templateAttr);
    if (currentKey === newKey) return;
    container.dataset.activeVariantTemplate = newKey;

    // Use template.content.cloneNode for safety with nested scripts/styles.
    const fragment = template.content
      ? template.content.cloneNode(true)
      : (() => {
          const tmp = document.createElement('div');
          tmp.innerHTML = template.innerHTML;
          const f = document.createDocumentFragment();
          while (tmp.firstChild) f.appendChild(tmp.firstChild);
          return f;
        })();

    container.innerHTML = '';
    container.appendChild(fragment);
  }

  updateButtonState(variant) {
    const button = document.querySelector('button[type="submit"].submit_add_cart');
    if (!button) return;

    const textSpan = button.querySelector('[data-atc-text-span]');
    if (!textSpan) return;

    // Handle null variant (unavailable/unmatched variant)
    if (variant === null || variant === undefined) {
      button.setAttribute('disabled', '');
      textSpan.textContent = 'Unavailable';
      return;
    }

    if (!variant.available) {
      button.setAttribute('disabled', '');
      textSpan.textContent = 'Sold out';
    } else {
      button.removeAttribute('disabled');
      textSpan.textContent = 'Add to cart';
    }
  }

  preselectVariantById() {
    const variantIdEl = document.querySelector('#product-variant-id');
    if (!variantIdEl) return false;

    let variantId = parseInt(variantIdEl.value, 10);
    let variant = null;
    if (variantId && !isNaN(variantId)) {
      variant = this.variants.find(v => v.id === variantId);
    }

    // When a Pack Bundle selector is present, #product-variant-id holds the bundle
    // variant ID (which is NOT in this.variants). Fall back to the canonical variant
    // ID exposed via a separate data island so we can still preselect Weight, etc.
    if (!variant) {
      const canonicalEl = document.querySelector('#json_canonical_variant_id');
      if (canonicalEl) {
        try {
          const canonicalId = parseInt(JSON.parse(canonicalEl.textContent), 10);
          if (canonicalId && !isNaN(canonicalId)) {
            variant = this.variants.find(v => v.id === canonicalId);
          }
        } catch (e) { /* ignore */ }
      }
    }

    if (!variant) return false;

    // Set all options in this.selected first before any UI updates or events
    this.options.forEach((opt, index) => {
      const optionName = opt.name;
      const optionValue = variant.options[index];
      this.selected[optionName] = optionValue;
    });

    // Update UI without dispatching events (to avoid triggering updateSelectedVariant multiple times)
    this.options.forEach((opt, index) => {
      const optionName = opt.name;
      const optionValue = variant.options[index];

      const radio = this.querySelector(`input[name="option_${optionName.toLowerCase()}"][value="${optionValue}"]`);
      if (radio) {
        radio.checked = true;
        
        // Update active color label
        const wrapper = radio.closest('.color-circles-active');
        const labelEl = wrapper?.querySelector('.active-color');
        if (labelEl) labelEl.textContent = optionValue;
      }

      const sizeBtn = this.querySelector(`.size-button[data-size="${optionValue}"]`);
      if (sizeBtn) {
        sizeBtn.classList.add('active');
      }

      // Sync chip state for non-color/non-size chip selectors (e.g. Weight Capacity).
      const chipGroup = this.querySelector(`.option-chips[data-option-block-name="${optionName}"]`);
      if (chipGroup) {
        chipGroup.querySelectorAll('.option-chip').forEach(c => {
          const isMatch = c.dataset.value === optionValue;
          c.classList.toggle('option-chip--active', isMatch);
          c.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
        });
      }

      this.querySelectorAll('.custom-select').forEach(select => {
        const trigger = select.querySelector('.select-trigger');
        const options = select.querySelectorAll('.option-value');
        options.forEach(opt => {
          if (opt.dataset.value === optionValue) {
            trigger.innerHTML = opt.innerHTML;
          }
        });
      });
    });

    // Trigger updateSelectedVariant once with all options set
    this.updateSelectedVariant();

    return true;
  }

  setupSellingPlanListener() {
    // Listen for selling plan changes to sync price display
    // Shopify's native subscription widget updates prices automatically
    // We need to ensure our price display stays in sync
    
    const sellingPlanSelect = document.querySelector('select[name="selling_plan"]');
    const sellingPlanInput = document.querySelector('input[name="selling_plan"]');
    const sellingPlanElement = sellingPlanSelect || sellingPlanInput;

    if (sellingPlanElement) {
      sellingPlanElement.addEventListener('change', () => {
        // Shopify's native widget will update the price
        // We just need to ensure our display elements are visible/updated
        setTimeout(() => {
          this.syncPriceFromShopifyWidget();
        }, 150);
      });
    }

    // Listen for Shopify's native price update events if available
    if (typeof document.addEventListener !== 'undefined') {
      document.addEventListener('shopify:price:update', () => {
        this.syncPriceFromShopifyWidget();
      });
    }

    // Also observe mutations on price elements in case Shopify updates them directly
    const priceBlock = document.querySelector('.price-block');
    if (priceBlock && typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(() => {
        // Price was updated by Shopify, ensure our display is in sync
        this.syncPriceFromShopifyWidget();
      });
      
      observer.observe(priceBlock, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
  }

  syncPriceFromShopifyWidget() {
    // Check if Shopify's native widget has updated the price
    // Look for price elements that might have been updated by Shopify
    const priceEl = document.querySelector('.price-block .price');
    const compareEl = document.querySelector('.price-block .cprice');
    const saveEl = document.querySelector('.price-block .priced');

    // If price element exists and has content, ensure compare and save elements are properly displayed
    if (priceEl && priceEl.textContent.trim()) {
      // Check if there's a compare price visible
      if (compareEl && compareEl.textContent.trim() && compareEl.style.display !== 'none') {
        // Calculate and update save percentage if needed
        if (saveEl) {
          const priceText = priceEl.textContent;
          const compareText = compareEl.textContent;
          
          // Extract numeric values
          const priceMatch = priceText.match(/[\d,]+\.?\d*/);
          const compareMatch = compareText.match(/[\d,]+\.?\d*/);
          
          if (priceMatch && compareMatch) {
            const price = parseFloat(priceMatch[0].replace(/,/g, ''));
            const compare = parseFloat(compareMatch[0].replace(/,/g, ''));
            if (compare > price) {
              const discount = Math.round(100 - (price / compare) * 100);
              saveEl.textContent = `save ${discount}%`;
              saveEl.style.display = '';
            }
          }
        }
      } else if (saveEl) {
        saveEl.style.display = 'none';
      }
    }
  }

  updatePriceDisplay(variant) {
    // Check if a selling plan is selected - if so, let Shopify's native widget handle price updates
    const sellingPlanSelect = document.querySelector('select[name="selling_plan"]');
    const sellingPlanInput = document.querySelector('input[name="selling_plan"]');
    const sellingPlanId = sellingPlanSelect?.value || sellingPlanInput?.value;
    
    if (sellingPlanId) {
      // Selling plan is selected - sync with Shopify's native price update
      // Don't override the price as Shopify's widget handles subscription pricing
      this.syncPriceFromShopifyWidget();
      return;
    }

    // No selling plan selected - use regular variant pricing
    const priceEl = document.querySelector('.price-block .price');
    const compareEl = document.querySelector('.price-block .cprice');
    const saveEl = document.querySelector('.price-block .priced');

    const price = variant.price;
    const compare = variant.compare_at_price;

    if (priceEl) {
      priceEl.textContent = this.formatMoney(price);
      priceEl.style.display = '';
    }

    if (compare && compare > price) {
      if (compareEl) {
        compareEl.textContent = this.formatMoney(compare);
        compareEl.style.display = '';
      }
      if (saveEl) {
        const discount = Math.round(100 - (price / compare) * 100);
        saveEl.textContent = `save ${discount}%`;
        saveEl.style.display = '';
      }
    } else {
      if (compareEl) compareEl.style.display = 'none';
      if (saveEl) saveEl.style.display = 'none';
    }
  }

  formatMoney(cents) {
    // Use Shopify's formatMoney function with shop's money format
    const moneyFormat = (window.theme && window.theme.settings && window.theme.settings.moneyFormat)
      || (window.CartJS && window.CartJS.settings && window.CartJS.settings.moneyFormat)
      || (window.Shopify && window.Shopify.money_format);

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }

    // Fallback to basic formatting if Shopify.formatMoney is not available
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  }
}

class PacSection extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.quantityInput = document.querySelector('quantity-inputs input[name="quantity"]');
    this.quantityWrapper = document.querySelector('quantity-inputs');

    if (this.quantityWrapper) {
      this.quantityWrapper.style.display = 'none';
    }

    this.addEventListener('click', this.handleClick.bind(this));

    // При загрузке активный пак
    const defaultActive = this.querySelector('.pack.active');
    if (defaultActive) {
      this.setQuantityFromPack(defaultActive);
    }
  }

  handleClick(event) {
    const clickedPack = event.target.closest('.pack');
    if (!clickedPack || !this.contains(clickedPack)) return;

    this.querySelectorAll('.pack').forEach(pack => {
      pack.classList.remove('active');
    });

    clickedPack.classList.add('active');
    this.setQuantityFromPack(clickedPack);
    // Вызываем кастомное событие (опционально)
    this.dispatchEvent(new CustomEvent('pack:change', {
      detail: {
        id: clickedPack.dataset.packId,
        quantity: clickedPack.querySelector('[data-packquntity]')?.dataset.packquntity
      },
      bubbles: true
    }));
  }

  setQuantityFromPack(packElement) {
    const quantityElement = packElement.querySelector('[data-packquntity]');
    const quantity = quantityElement?.dataset.packquntity;

    if (this.quantityInput && quantity) {
      this.quantityInput.value = quantity;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}


class QuantityInputs extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this._buttonClickInProgress = false;
    this._lastClickTime = 0;
  }

  connectedCallback() {
    if (this.initialized) return; // 🔐 Защита от повторного вызова
    this.initialized = true;

    this.input = this.querySelector('input[name="quantity"]');
    this.plusBtn = this.querySelector('button[name="plus"]');
    this.minusBtn = this.querySelector('button[name="minus"]');

    if (!this.input || !this.plusBtn || !this.minusBtn) return;

    this.plusBtn.addEventListener('click', (e) => this.increment(e));
    this.minusBtn.addEventListener('click', (e) => this.decrement(e));
    this.input.addEventListener('change', () => this.updateButtonStates());
    this.input.addEventListener('input', () => this.updateButtonStates());

    // Initialize button states
    this.updateButtonStates();
  }

  disableButtons() {
    if (this.plusBtn) {
      this.plusBtn.disabled = true;
      this.plusBtn.style.pointerEvents = 'none';
      this.plusBtn.style.opacity = '0.6';
    }
    if (this.minusBtn) {
      this.minusBtn.disabled = true;
      this.minusBtn.style.pointerEvents = 'none';
      this.minusBtn.style.opacity = '0.6';
    }
  }

  enableButtons() {
    if (this.plusBtn) {
      this.plusBtn.disabled = false;
      this.plusBtn.style.pointerEvents = '';
      this.plusBtn.style.opacity = '';
    }
    if (this.minusBtn) {
      this.minusBtn.disabled = false;
      this.minusBtn.style.pointerEvents = '';
      this.minusBtn.style.opacity = '';
    }
  }

  getMinValue() {
    const minValue = this.input.getAttribute('min');
    return minValue ? parseInt(minValue, 10) : 1;
  }

  updateButtonStates() {
    const current = parseInt(this.input.value, 10) || this.getMinValue();
    const minValue = this.getMinValue();

    // Disable minus button if current value is at or below minimum
    if (current <= minValue) {
      this.minusBtn.setAttribute('disabled', 'disabled');
    } else {
      this.minusBtn.removeAttribute('disabled');
    }
  }

  increment(e) {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Debounce: prevent clicks within 300ms
    const now = Date.now();
    if (now - this._lastClickTime < 300) {
      return;
    }
    this._lastClickTime = now;
    
    // Prevent multiple rapid clicks
    if (this._buttonClickInProgress) {
      return;
    }
    
    this._buttonClickInProgress = true;
    this.disableButtons();
    
    const current = parseInt(this.input.value, 10) || this.getMinValue();
    this.input.value = current + 1;
    
    // Dispatch change event
    this.input.dispatchEvent(new Event('change'));
    this.updateButtonStates();
    
    // Re-enable buttons after debounce delay
    setTimeout(() => {
      this._buttonClickInProgress = false;
      this.enableButtons();
    }, 300);
  }

  decrement(e) {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Debounce: prevent clicks within 300ms
    const now = Date.now();
    if (now - this._lastClickTime < 300) {
      return;
    }
    this._lastClickTime = now;
    
    // Prevent multiple rapid clicks
    if (this._buttonClickInProgress) {
      return;
    }
    
    this._buttonClickInProgress = true;
    this.disableButtons();
    
    const current = parseInt(this.input.value, 10) || this.getMinValue();
    const minValue = this.getMinValue();
    const newValue = Math.max(current - 1, minValue);
    this.input.value = newValue;
    
    // Dispatch change event
    this.input.dispatchEvent(new Event('change'));
    this.updateButtonStates();
    
    // Re-enable buttons after debounce delay
    setTimeout(() => {
      this._buttonClickInProgress = false;
      this.enableButtons();
    }, 300);
  }
}


class GiftBlock extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.input = document.querySelector('input[name="quantity"]');
    this.observeInputChanges();
    this.observeVariantChange();
    this.observePackChanges();
    this.checkGifts();
  }

  observeInputChanges() {
    if (!this.input) return;
    this.input.addEventListener('input', () => this.checkGifts());
    this.input.addEventListener('change', () => this.checkGifts());
  }

  observeVariantChange() {
    const variantInput = document.querySelector('#product-variant-id');
    if (variantInput) {
      variantInput.addEventListener('change', () => this.checkGifts());
    }
  }

  observePackChanges() {
    const pacSection = document.querySelector('pac-section');
    if (pacSection) {
      pacSection.addEventListener('pack:change', () => this.checkGifts());
    }
  }

  async fetchCartData() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error('Failed to fetch cart');
      const data = await res.json();
      return {
        quantity: data.items.reduce((total, item) => total + item.quantity, 0),
        total_price: data.items.reduce((total, item) => total + item.quantity * item.final_price, 0) // accurate item pricing
      };
    } catch (e) {
      return { quantity: 0, total_price: 0 };
    }
  }

  getSelectedVariantPriceFromJson() {
    const variantIdEl = document.querySelector('#product-variant-id');
    const jsonScript = document.querySelector('#json_prod_var');
    if (!variantIdEl || !jsonScript) return 0;

    try {
      const variantId = parseInt(variantIdEl.value);
      const variants = JSON.parse(jsonScript.textContent);
      const selected = variants.find(v => v.id === variantId);
      return selected ? selected.price : 0;
    } catch (e) {
      return 0;
    }
  }

  async checkGifts() {
    const gifts = this.querySelectorAll('.gift');
    const cart = await this.fetchCartData();
    const localQty = parseInt(this.input?.value || '0', 10);
    const selectedPrice = this.getSelectedVariantPriceFromJson();
    const localTotal = localQty * selectedPrice;
    const combinedQty = localQty + cart.quantity;
    const combinedTotal = localTotal + cart.total_price;

    gifts.forEach(gift => {
      const giftId = gift.dataset.giftid;
      if (!giftId || giftId === '0') return this.lockGift(gift);

      const area = gift.dataset.giftarea;
      const rule = gift.dataset.unlockrulle || 'price';
      const qtyUnlock = parseInt(gift.dataset.qtyunlock || '0', 10);
      const priceUnlock = parseInt(gift.dataset.priceunlock || '0', 10);

      let isUnlocked = false;

      if (area === 'global') {
        if (rule === 'quntity') {
          isUnlocked = qtyUnlock > 0 && combinedQty >= qtyUnlock;
        } else if (rule === 'price') {
          isUnlocked = priceUnlock > 0 && combinedTotal >= priceUnlock;
        }
      } else if (area === 'local') {
        if (rule === 'quntity') {
          isUnlocked = qtyUnlock > 0 && localQty >= qtyUnlock;
        } else if (rule === 'price') {
          isUnlocked = priceUnlock > 0 && localTotal >= priceUnlock;
        }
      }

      if (isUnlocked) {
        this.unlockGift(gift);
      } else {
        this.lockGift(gift);
      }
    });
  }
  unlockGift(gift) {
    gift.dataset.locked = 'false';
    const customImg = gift.dataset.giftimg?.trim();
    const productImg = gift.dataset.giftproductimg?.trim();

    // Check if URL is valid (not empty and has actual path before query params)
    const isValidUrl = (url) => {
      if (!url || url === '') return false;
      // Check for Liquid error messages
      if (url.includes('Liquid error') || url.includes('invalid url input')) return false;
      // Shopify returns URLs like "?width=400" when no image exists
      if (url.startsWith('?')) return false;
      // Check if there's actual content before the query params
      const pathPart = url.split('?')[0];
      return pathPart && pathPart.length > 0;
    };

    const imgSrc = isValidUrl(customImg) ? customImg :
      isValidUrl(productImg) ? productImg : null;

    const wrapper = gift.querySelector('.gift-image');

    if (imgSrc && wrapper) {
      // Add onerror handler to show placeholder if image fails to load
      wrapper.innerHTML = `<img src="${imgSrc}" alt="Gift" onerror="this.style.display='none'; this.parentElement.innerHTML='<svg width=\\'60\\' height=\\'60\\' viewBox=\\'0 0 60 60\\' fill=\\'none\\'><rect width=\\'60\\' height=\\'60\\' fill=\\'#f0f0f0\\'/><path d=\\'M30 20v20M20 30h20\\' stroke=\\'#999\\' stroke-width=\\'2\\'/></svg><div style=\\'color: #666; font-size: 12px; margin-top: 8px;\\'>Gift Image</div>';">`;
    } else if (wrapper) {
      // No valid image URL, show placeholder
      wrapper.innerHTML = `<svg width="60" height="60" viewBox="0 0 60 60" fill="none"><rect width="60" height="60" fill="#f0f0f0"/><path d="M30 20v20M20 30h20" stroke="#999" stroke-width="2"/></svg><div style="color: #666; font-size: 12px; margin-top: 8px;">Gift Image</div>`;
    }
  }


  lockGift(gift) {
    gift.dataset.locked = 'true';
    const wrapper = gift.querySelector('.gift-image');
    const maney = gift.dataset.priceunlock;
    const customLockImg = this.dataset.lockimg?.trim();

    if (wrapper) {
      if (customLockImg && customLockImg !== '') {
        // Use custom lock image
        wrapper.innerHTML = this.lockCustomImage(customLockImg, maney);
      } else {
        // Use default SVG lock icon
        wrapper.innerHTML = this.lockSVG(maney);
      }
    }
  }

  lockCustomImage(imgSrc, maney) {
    return `<img src="${imgSrc}" alt="Locked" style="max-width: 100px; max-height: 100px;">
            <div>Spend ${Shopify.formatMoney(maney, CartJS.settings.moneyFormat)} to unlock</div>`;
  }

  lockSVG(maney) {
    this
    return `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="23.5" fill="white" stroke="black"/>
            <path d="M30.75 34.3184H17.25C16.6533 34.3184 16.081 34.0813 15.659 33.6593C15.2371 33.2374 15 32.6651 15 32.0684V23.8184C15 23.2216 15.2371 22.6493 15.659 22.2274C16.081 21.8054 16.6533 21.5684 17.25 21.5684H30.75C31.3467 21.5684 31.919 21.8054 32.341 22.2274C32.7629 22.6493 33 23.2216 33 23.8184V32.0684C33 32.6651 32.7629 33.2374 32.341 33.6593C31.919 34.0813 31.3467 34.3184 30.75 34.3184ZM17.25 23.0684C17.0511 23.0684 16.8603 23.1474 16.7197 23.288C16.579 23.4287 16.5 23.6194 16.5 23.8184V32.0684C16.5 32.2673 16.579 32.458 16.7197 32.5987C16.8603 32.7393 17.0511 32.8184 17.25 32.8184H30.75C30.9489 32.8184 31.1397 32.7393 31.2803 32.5987C31.421 32.458 31.5 32.2673 31.5 32.0684V23.8184C31.5 23.6194 31.421 23.4287 31.2803 23.288C31.1397 23.1474 30.9489 23.0684 30.75 23.0684H17.25Z" fill="#101820"/>
            <path d="M30 23.0684H18C17.8011 23.0684 17.6103 22.9893 17.4697 22.8487C17.329 22.708 17.25 22.5173 17.25 22.3184V17.8184C17.25 16.2271 17.8821 14.7009 19.0074 13.5757C20.1326 12.4505 21.6587 11.8184 23.25 11.8184H24.75C26.3413 11.8184 27.8674 12.4505 28.9926 13.5757C30.1179 14.7009 30.75 16.2271 30.75 17.8184V22.3184C30.75 22.5173 30.671 22.708 30.5303 22.8487C30.3897 22.9893 30.1989 23.0684 30 23.0684ZM18.75 21.5684H29.25V17.8184C29.25 16.6249 28.7759 15.4803 27.932 14.6364C27.0881 13.7925 25.9435 13.3184 24.75 13.3184H23.25C22.0565 13.3184 20.9119 13.7925 20.068 14.6364C19.2241 15.4803 18.75 16.6249 18.75 17.8184V21.5684Z" fill="#101820"/>
            <path d="M24 28.3184C23.7033 28.3184 23.4133 28.2304 23.1666 28.0656C22.92 27.9007 22.7277 27.6665 22.6142 27.3924C22.5006 27.1183 22.4709 26.8167 22.5288 26.5257C22.5867 26.2348 22.7296 25.9675 22.9393 25.7577C23.1491 25.5479 23.4164 25.4051 23.7074 25.3472C23.9983 25.2893 24.2999 25.319 24.574 25.4325C24.8481 25.5461 25.0824 25.7383 25.2472 25.985C25.412 26.2317 25.5 26.5217 25.5 26.8184C25.5 27.2162 25.342 27.5977 25.0607 27.879C24.7794 28.1603 24.3978 28.3184 24 28.3184Z" fill="#101820"/>
            <path d="M24.75 27.5684H23.25V30.5684H24.75V27.5684Z" fill="#101820"/>
          </svg>
          <div>Spend ${Shopify.formatMoney(maney, CartJS.settings.moneyFormat)} to unlock</div>
          `;
  }
}

// Helper function to update cart icon directly (fix for cart icon update issue)
function updateCartIconDirectly(itemCount) {
  const cartCountBubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
  const headerCartCount = document.querySelector('.header__cart-count');
  const cartLinkBubble = document.querySelector('.cart-link__bubble');
  
  if (cartCountBubble) {
    if (itemCount > 0 && itemCount < 100) {
      cartCountBubble.textContent = itemCount;
    } else if (itemCount >= 100) {
      cartCountBubble.textContent = '99+';
    } else {
      cartCountBubble.textContent = '';
    }
  }
  
  if (headerCartCount) {
    headerCartCount.textContent = itemCount || 0;
  }
  
  if (cartLinkBubble) {
    if (itemCount > 0) {
      cartLinkBubble.classList.add('cart-link__bubble--visible');
    } else {
      cartLinkBubble.classList.remove('cart-link__bubble--visible');
    }
  }
}

class AddCardProd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const form = this.querySelector('form.form_add_cartp');
    if (!form) return;

    // Create error message container if it doesn't exist
    let errorContainer = form.querySelector('.cart-error-message');
    if (!errorContainer) {
      errorContainer = document.createElement('div');
      errorContainer.className = 'cart-error-message';
      errorContainer.setAttribute('role', 'alert');
      errorContainer.setAttribute('aria-live', 'polite');
      // Apply inline styles for error message - stacked below button
      errorContainer.style.cssText = `
        display: none;
        margin-top: 12px;
        padding: 12px 16px;
        background-color: #fff5f5;
        border: 1px solid #feb2b2;
        border-left: 4px solid #f56565;
        border-radius: 6px;
        color: #c53030;
        font-size: 14px;
        line-height: 1.5;
        font-weight: 400;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        width: 100%;
        clear: both;
      `;
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        // Find the button's parent container (could be add-card-prod or a wrapper)
        let insertTarget = submitBtn.parentElement;

        // If button is inside add-card-prod, insert after add-card-prod
        const addCardProd = submitBtn.closest('add-card-prod');
        if (addCardProd && addCardProd.parentNode) {
          insertTarget = addCardProd;
        }

        // Insert error container after the target element to ensure it's below
        if (insertTarget && insertTarget.parentNode) {
          insertTarget.parentNode.insertBefore(errorContainer, insertTarget.nextSibling);
        } else {
          // Fallback: append to form
          form.appendChild(errorContainer);
        }
      } else {
        form.appendChild(errorContainer);
      }
    }

    const showError = (message) => {
      errorContainer.textContent = message;
      errorContainer.style.display = 'block';
      // Scroll to error if needed
      errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const hideError = () => {
      errorContainer.style.display = 'none';
      errorContainer.textContent = '';
    };

    const resetQuantity = () => {
      const quantityInput = document.querySelector('quantity-inputs input[name="quantity"]');
      if (quantityInput) {
        const minValue = quantityInput.getAttribute('min');
        const resetValue = minValue ? parseInt(minValue, 10) : 1;
        quantityInput.value = resetValue;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Clear any previous errors
      hideError();

      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      const variantInput = document.querySelector('#product-variant-id');
      const quantityInput = document.querySelector('quantity-inputs input[name="quantity"]');
      const variantId = variantInput?.value;
      const qta = parseInt(quantityInput?.value || '1', 10);

      if (!variantInput || variantInput.disabled || !variantId || isNaN(qta) || qta <= 0) {
        showError('Please select a valid product variant and quantity.');
        resetQuantity();
        if (submitBtn) submitBtn.disabled = false;
        return;
      }

      const items = [];

      // ✅ Get selling plan if selected (check within form first, then globally)
      let sellingPlanId = null;
      const sellingPlanSelect = form.querySelector('select[name="selling_plan"]') || document.querySelector('select[name="selling_plan"]');
      const sellingPlanInput = form.querySelector('input[name="selling_plan"]') || document.querySelector('input[name="selling_plan"]');
      
      if (sellingPlanSelect) {
        sellingPlanId = sellingPlanSelect.value;
      } else if (sellingPlanInput) {
        sellingPlanId = sellingPlanInput.value;
      }
      
      // Also check for radio buttons (some subscription apps use radio inputs)
      if (!sellingPlanId) {
        const sellingPlanRadio = form.querySelector('input[name="selling_plan"]:checked') || document.querySelector('input[name="selling_plan"]:checked');
        if (sellingPlanRadio) {
          sellingPlanId = sellingPlanRadio.value;
        }
      }

      // ✅ Collect line-item properties from the form (`properties[KEY]` inputs).
      // The form is submitted via fetch() (not native submit), so anything beyond
      // `id` and `quantity` must be picked up explicitly here. We need this so
      // the hidden `properties[_image]` snapshot — which the cart drawer relies
      // on to display the parent variant's image for pack-bundle child items —
      // is forwarded to /cart/add.js.
      const collectFormProperties = () => {
        const props = {};
        form.querySelectorAll('input[name^="properties["], textarea[name^="properties["]').forEach((el) => {
          const match = el.name.match(/^properties\[([^\]]+)\]$/);
          if (!match) return;
          const key = match[1];
          const value = (el.value || '').toString();
          if (!value) return; // skip empty values to avoid noisy line items
          props[key] = value;
        });
        return Object.keys(props).length > 0 ? props : null;
      };

      // ✅ Add main product
      const mainVariant = parseInt(variantId, 10);
      const mainProps = collectFormProperties();
      if (!isNaN(mainVariant)) {
        const mainItem = { id: mainVariant, quantity: qta };
        // Add selling plan if selected (must be a valid non-empty value)
        if (sellingPlanId && sellingPlanId !== '' && sellingPlanId !== '0' && sellingPlanId !== 'null') {
          mainItem.selling_plan = sellingPlanId;
        }
        if (mainProps) {
          mainItem.properties = mainProps;
        }
        items.push(mainItem);
      } else {
        showError('Main product variant is invalid.');
        resetQuantity();
        if (submitBtn) submitBtn.disabled = false;
        return;
      }

      // ✅ Pack-bundle replacement: if the new item is a pack bundle, remove any
      // existing pack bundle for the SAME canonical product AND the SAME weight
      // capacity before adding. The canonical product can host multiple weight
      // variants (e.g. 160 lbs → "Safety Bars", 240 lbs → "Bath Handle Premium"),
      // and those are treated as DIFFERENT products from the cart's perspective —
      // they coexist. Only different pack sizes within the same weight collapse
      // into a single line (so 2-pack → 4-pack of the same weight = upgrade).
      //
      // Without this, adding the same bundle twice produces a doubled qty
      // (2 × $109.99 = $219.98) which doesn't match any real pack price.
      const isPackBundle = mainProps && mainProps._pack_bundle === 'true';
      if (isPackBundle && typeof CartJS !== 'undefined' && CartJS.cart && Array.isArray(CartJS.cart.items)) {
        const newCanonical = mainProps._pack_canonical_url || '';
        const newWeight = (mainProps._pack_weight || '').trim();
        const updates = {};
        CartJS.cart.items.forEach((existing) => {
          const existingProps = existing.properties || {};
          const existingIsBundle =
            existingProps._pack_bundle === 'true' ||
            existingProps._bundle_id ||
            existingProps._bundle_path;
          if (!existingIsBundle) return;
          const existingCanonical = existingProps._pack_canonical_url || '';
          const existingWeight = (existingProps._pack_weight || '').trim();
          // Same exact bundle variant → always collapse (defense against duplicate ATC).
          // Same canonical + same weight, different pack size → upgrade/downgrade.
          // Different canonical OR different weight → leave alone (they coexist).
          const sameVariant = existing.variant_id === mainVariant;
          const sameCanonicalAndWeight =
            newCanonical && existingCanonical === newCanonical &&
            newWeight && existingWeight === newWeight;
          if (sameVariant || sameCanonicalAndWeight) {
            updates[existing.variant_id] = 0;
          }
        });
        if (Object.keys(updates).length > 0) {
          try {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates }),
            });
          } catch (e) {
            // Non-fatal: fall through to /cart/add.js. If the existing line wasn't
            // removed, the post-add hook in cart-slide will still cap qty at 1.
          }
        }
      }

      // ✅ Add unlocked gifts if present
      document.querySelectorAll('.gift[data-locked="false"]').forEach(gift => {
        const giftId = gift.dataset.giftid;
        if (giftId && giftId !== '0') {
          const parsedId = parseInt(giftId, 10);
          if (!isNaN(parsedId)) {
            const giftItem = { id: parsedId, quantity: 1 };
            items.push(giftItem);
          }
        }
      });

      // ✅ Add checked addons if present
      // Search in the entire document, not just within the form, to catch addons in product sections
      const addonItems = document.querySelectorAll('.product-addons__item:not(.product-addons__item--placeholder)');

      addonItems.forEach((addon, index) => {
        const checkbox = addon.querySelector('.product-addons__toggle');
        const addonId = addon.dataset.variantId || addon.getAttribute('data-variant-id');

        if (checkbox && !checkbox.disabled && checkbox.checked && addonId) {
          const parsedAddonId = parseInt(addonId, 10);
          if (!isNaN(parsedAddonId)) {
            const addonItem = { id: parsedAddonId, quantity: 1 };
            items.push(addonItem);
          }
        }
      });

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const errorData = await response.json();

          // Extract error message from Shopify response
          let errorMessage = 'Unable to add items to cart.';
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.description) {
            errorMessage = errorData.description;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }

          // Show user-friendly error message below the button
          showError(errorMessage);
          resetQuantity();
          if (submitBtn) submitBtn.disabled = false;
          return;
        }

        const result = await response.json();

        // Clear any errors on success
        hideError();

        // Refresh CartJS to update cart drawer (fix for theme editor issue)
        if (typeof CartJS !== 'undefined' && typeof CartJS.getCart === 'function') {
          CartJS.getCart({
            success: (cart) => {
              // CRITICAL: Update CartJS.cart object properly for Rivets.js (fix for theme editor items not showing)
              // Rivets.js needs to see the change, so we update in place rather than replacing the object
              if (cart) {
                if (!CartJS.cart) {
                  CartJS.cart = {};
                }
                // Update all cart properties in place
                Object.keys(cart).forEach(key => {
                  if (key === 'items' && Array.isArray(cart.items)) {
                    // For items array, replace it completely to trigger Rivets.js rv-each
                    CartJS.cart.items = cart.items;
                  } else {
                    CartJS.cart[key] = cart[key];
                  }
                });
              }
              
              // Update cart icon immediately after adding product (fix for cart icon update issue)
              const cartSlide = document.querySelector('cart-slide');
              if (cartSlide && typeof cartSlide.updateCartIconCount === 'function') {
                cartSlide.updateCartIconCount(cart.item_count || 0);
              } else {
                // Fallback: update cart icon directly if cart-slide is not available
                updateCartIconDirectly(cart.item_count || 0);
              }
              
              // Trigger cart.requestComplete event for Rivets.js updates (after CartJS.cart is updated)
              if (typeof jQuery !== 'undefined') {
                jQuery(document).trigger('cart.requestComplete', [cart]);
              }
              
              // Refresh and open cart drawer (fix for theme editor PDP issue)
              if (cartSlide) {
                // Refresh cart drawer immediately
                if (typeof cartSlide.refreshCart === 'function') {
                  cartSlide.refreshCart();
                }
                
                // Use a delay to ensure Rivets.js updates before opening (theme editor fix)
                // Longer delay for theme editor to ensure proper updates
                const delay = (typeof Shopify !== 'undefined' && Shopify.designMode) ? 500 : 200;
                setTimeout(() => {
                  // Force refresh again to ensure Rivets.js has updated (theme editor fix)
                  if (typeof cartSlide.refreshCart === 'function') {
                    cartSlide.refreshCart();
                  }
                  // In theme editor, trigger another cart.requestComplete to force Rivets.js update
                  if (typeof Shopify !== 'undefined' && Shopify.designMode && cart) {
                    if (typeof CartJS !== 'undefined') {
                      CartJS.cart = cart;
                    }
                    if (typeof jQuery !== 'undefined') {
                      jQuery(document).trigger('cart.requestComplete', [cart]);
                    }
                  }
                  // Open cart drawer if it's not already open
                  if (typeof cartSlide.toggleCart === 'function' && !cartSlide.classList.contains('active')) {
                    cartSlide.toggleCart();
                  }
                }, delay);
              }
            }
          });
        } else {
          // Fallback: update cart icon even if CartJS is not available
          if (result && result.item_count !== undefined) {
            updateCartIconDirectly(result.item_count);
          } else {
            // Fetch cart to get item count
            fetch('/cart.js')
              .then(response => response.json())
              .then(cart => {
                updateCartIconDirectly(cart.item_count || 0);
                // Try to refresh cart drawer even without CartJS
                const cartSlide = document.querySelector('cart-slide');
                if (cartSlide) {
                  if (typeof cartSlide.refreshCart === 'function') {
                    cartSlide.refreshCart();
                  }
                  if (typeof cartSlide.toggleCart === 'function' && !cartSlide.classList.contains('active')) {
                    cartSlide.toggleCart();
                  }
                }
              })
              .catch(() => {
                // Silently fail
              });
          }
        }
        resetQuantity();
        if (submitBtn) submitBtn.disabled = false;
      } catch (error) {
        showError('An error occurred while adding items to cart. Please try again.');
        resetQuantity();
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  }
}

/**
 * <pack-bundle-section> — pack-size selector backed by hidden Shopify Bundle products.
 *
 * Liquid renders one card per bundle reference (custom.pack_bundles list metafield).
 * Each card carries data-* attributes describing the bundle variant. This element:
 *   1. Filters cards to the currently selected Weight (data-current-weight).
 *   2. Marks the smallest-pack card as the default selection.
 *   3. On click, writes the bundle variant ID into the ATC form, updates the displayed
 *      price, and toggles the submit button's disabled state based on availability.
 *   4. Listens to `pack:weight-change` (dispatched by customVariant) and re-runs the
 *      filter + default-selection step for the new weight.
 */
class packBundleSection extends HTMLElement {
  constructor() {
    super();
    this.bundles = [];
    this.currentWeight = '';
  }

  connectedCallback() {
    this.bundles = this.readBundles();
    this.currentWeight = this.dataset.currentWeight || '';
    this.cards = Array.from(this.querySelectorAll('[data-pack-card]'));

    if (!this.cards.length) return;

    this.cards.forEach(card => {
      card.addEventListener('click', (e) => this.onCardClick(e, card));
    });

    document.addEventListener('pack:weight-change', (e) => {
      const newWeight = e?.detail?.weight;
      if (!newWeight) return;
      this.currentWeight = newWeight;
      this.applyWeight(newWeight);
    });

    // Hide the manual quantity stepper — pack cards are the quantity selector.
    const stepper = document.querySelector('quantity-inputs.cart-quantity');
    if (stepper) stepper.style.display = 'none';

    // Sync ATC + price to the currently-active card so JS state matches Liquid SSR.
    const initiallyActive = this.cards.find(c => c.classList.contains('pack-bundle-card--active'));
    if (initiallyActive) {
      this.applyCard(initiallyActive, { skipMarkActive: true });
    }
  }

  readBundles() {
    const el = document.querySelector('#json_pack_bundles');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; }
    catch (e) { return []; }
  }

  onCardClick(event, card) {
    if (card.disabled || card.classList.contains('pack-bundle-card--soldout')) return;
    event.preventDefault();
    this.applyCard(card);
  }

  /**
   * Switch the visible cards when the customer changes the Weight option,
   * and auto-select the FIRST available pack listed for that weight (matches
   * the SSR default in `yas-product-section.liquid`).
   */
  applyWeight(weight) {
    let firstCard = null;

    this.cards.forEach(card => {
      const cardWeight = card.dataset.packWeight;
      if (cardWeight === weight) {
        card.hidden = false;
        card.style.display = '';
        const isAvailable = card.dataset.packAvailable === 'true';
        if (isAvailable && !firstCard) {
          firstCard = card;
        }
      } else {
        card.hidden = true;
        card.style.display = 'none';
        card.classList.remove('pack-bundle-card--active');
        card.setAttribute('aria-pressed', 'false');
      }
    });

    if (firstCard) {
      this.applyCard(firstCard);
    } else {
      // No available pack for this weight — disable ATC.
      this.setSubmitEnabled(false, 'Sold out');
    }
  }

  /**
   * Make `card` the active selection: write its variant ID into the ATC form,
   * update the price display, and toggle button enabled-state.
   */
  applyCard(card, { skipMarkActive = false } = {}) {
    if (!skipMarkActive) {
      this.cards.forEach(c => {
        c.classList.remove('pack-bundle-card--active');
        c.setAttribute('aria-pressed', 'false');
      });
      card.classList.add('pack-bundle-card--active');
      card.setAttribute('aria-pressed', 'true');
    }

    const variantId = card.dataset.variantId;
    const price = parseInt(card.dataset.packPrice, 10);
    const compare = parseInt(card.dataset.packCompare, 10) || 0;
    const isAvailable = card.dataset.packAvailable === 'true';
    const packSize = card.dataset.packSize;
    const packWeight = card.dataset.packWeight;

    // Sync both hidden id inputs (ATC form + installment form).
    document.querySelectorAll('input[name="id"]').forEach(input => {
      input.value = variantId;
      input.dispatchEvent(new Event('change'));
      if (isAvailable) input.removeAttribute('disabled');
      else input.setAttribute('disabled', 'disabled');
    });

    // Mark this line item as a pack bundle so the cart UI can disable the qty
    // stepper (preventing 1 → 2 increments that produce wrong prices like
    // 2 × $109.99 = $219.98 instead of the real next-pack-up at $219.99).
    // We attach our OWN line-item properties — Shopify Bundles' native
    // `_bundle_id` is not always present, and tag-based detection requires
    // merchant-side configuration. Doing it here makes detection 100% reliable.
    this.syncPackBundleProperties({ packSize: packSize, packWeight: packWeight });

    this.updatePackPriceDisplay(price, compare);
    this.setSubmitEnabled(isAvailable, isAvailable ? null : 'Sold out');
  }

  /**
   * Inject (or update) hidden `properties[_pack_bundle]` and `properties[_pack_size]`
   * inputs into every product form on the page. Cart drawer + cart page detection
   * code keys off these properties, so this is the single source of truth.
   */
  syncPackBundleProperties({ packSize, packWeight }) {
    const canonicalUrl = this.dataset.canonicalUrl || window.location.pathname;
    const forms = document.querySelectorAll('form[action*="/cart/add"], .form_add_cartp, .main-product_installment-form');
    forms.forEach(form => {
      this._upsertHiddenInput(form, 'properties[_pack_bundle]', 'true');
      if (packSize) this._upsertHiddenInput(form, 'properties[_pack_size]', String(packSize));
      if (packWeight) this._upsertHiddenInput(form, 'properties[_pack_weight]', String(packWeight));
      if (canonicalUrl) this._upsertHiddenInput(form, 'properties[_pack_canonical_url]', canonicalUrl);
    });
  }

  _upsertHiddenInput(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  updatePackPriceDisplay(price, compare) {
    const priceEl = document.querySelector('.price-block .price');
    const compareEl = document.querySelector('.price-block .cprice');
    const saveEl = document.querySelector('.price-block .priced');

    if (priceEl) priceEl.textContent = this.formatMoney(price);

    if (compare && compare > price) {
      if (compareEl) {
        compareEl.textContent = this.formatMoney(compare);
        compareEl.style.display = '';
      }
      if (saveEl) {
        const discount = Math.round(100 - (price / compare) * 100);
        saveEl.textContent = `Save ${discount}%`;
        saveEl.style.display = '';
      }
    } else {
      if (compareEl) compareEl.style.display = 'none';
      if (saveEl) saveEl.style.display = 'none';
    }
  }

  setSubmitEnabled(enabled, soldOutText) {
    const submit = document.querySelector('.submit_add_cart');
    if (!submit) return;
    if (enabled) {
      submit.removeAttribute('disabled');
    } else {
      submit.setAttribute('disabled', 'disabled');
      const span = submit.querySelector('.submit_add_cart_text');
      if (span && soldOutText) span.textContent = soldOutText;
    }
  }

  formatMoney(cents) {
    const moneyFormat = (window.theme && window.theme.settings && window.theme.settings.moneyFormat)
      || (window.CartJS && window.CartJS.settings && window.CartJS.settings.moneyFormat)
      || (window.Shopify && window.Shopify.money_format);

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }
}

/**
 * Deep-link the canonical PDP from a "?weight=160 lbs&pack=4" URL (used by the
 * cart drawer's "Edit pack" button so the customer lands back on the product
 * page with their previous selection already applied).
 *
 * Runs after customVariant + packBundleSection have connected. We fire chip +
 * card click events rather than mutating internal state directly so all the
 * existing handlers (price update, ATC form sync, _pack_* property emit) run
 * naturally — keeping a single source of truth for selection logic.
 */
function applyPackBundleUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const weight = params.get('weight');
  const pack = params.get('pack');
  if (!weight && !pack) return;

  // 1. Click the matching Weight Capacity chip (if URL specifies one and it differs).
  if (weight) {
    const chip = document.querySelector(
      `.option-chip[data-value="${CSS && CSS.escape ? CSS.escape(weight) : weight}"]`
    );
    if (chip && !chip.classList.contains('option-chip--active')) {
      chip.click();
    }
  }

  // 2. After the pack section has filtered cards for the new weight (the chip
  //    click triggers updateSelectedVariant → pack:weight-change → applyWeight
  //    which auto-picks the smallest pack). Wait one frame, then click the
  //    requested pack size.
  if (pack) {
    requestAnimationFrame(() => {
      setTimeout(() => {
        const card = document.querySelector(
          `.pack-bundle-card[data-pack-size="${pack}"]:not([hidden])`
        );
        if (card && !card.classList.contains('pack-bundle-card--active')) {
          card.click();
        }
      }, 50);
    });
  }
}

document.addEventListener("DOMContentLoaded", (event) => {


  customElements.define('custom-variant', customVariant);
  customElements.define('pac-section', PacSection);
  if (!customElements.get('pack-bundle-section')) {
    customElements.define('pack-bundle-section', packBundleSection);
  }
  // After all custom elements are upgraded and connectedCallback has run,
  // apply any ?weight=... &pack=... deep-link params from the URL.
  setTimeout(applyPackBundleUrlParams, 100);
  // 🛡 Проверка: не регистрировать повторно, если уже определено
  if (!customElements.get('quantity-inputs')) {
    customElements.define('quantity-inputs', QuantityInputs);
  }

  if (!customElements.get('gift-block')) {
    customElements.define('gift-block', GiftBlock);
  }

  if (!customElements.get('add-card-prod')) {
    customElements.define('add-card-prod', AddCardProd);
  }

})